import classNames from 'classnames';
import type { Op } from 'quill/core';
import React from 'react';
import type { JSX } from 'react';
import { MOD_KEY } from '../../constants/os';
import LinkCursorSelection from '../../editor/LinkCursorSelection';
import QuillComponent from '../../editor/QuillComponent';
import { useQuill, withQuillContextProvider } from '../../editor/QuillContext';
import type QuillEditor from '../../editor/QuillEditor';
import EmojiAutocompleter from '../../editor/controls/EmojiAutocompleter';
import MentionPicker from '../../editor/controls/MentionPicker';
import LinkOnlyEditorToolbar from '../../editor/controls/toolbar/LinkOnlyEditorToolbar';
import useLinkShortcut from '../../editor/useLinkShortcut';
import { insertPrefix, insertText } from '../../utils/quillHelpers';
import useEventListener from '../../utils/useEventListener';
import * as styles from './CommentInput.module.scss';

interface CommentInputConsumerViewProps {
  quill: QuillEditor | null;
  initialContent: Op[];
  autoFocus: boolean;
  onSave: () => void;
  onTextChange: (newContent: Op[]) => void;
  disabled: boolean;
}

const CommentInputConsumerView = ({
  quill,
  initialContent,
  autoFocus,
  onSave,
  onTextChange,
  disabled,
}: CommentInputConsumerViewProps) => {
  useLinkShortcut();

  useEventListener(
    quill?.root,
    'keydown',
    (event) => {
      if (event.key === 'Enter' && event[MOD_KEY]) {
        event.stopPropagation();
        event.preventDefault();
        onSave();
      }
    },
    {},
  );

  return (
    <div className={classNames('comment-editor', styles.container)}>
      <div className={styles.content}>
        {quill && (
          <>
            <LinkOnlyEditorToolbar quill={quill} />
            <EmojiAutocompleter quill={quill} readOnly={false} />
          </>
        )}
        <QuillComponent
          noTitleLine
          namespace="comment"
          contentPlaceholder="Speak your mind"
          moduleConfig={{ prefixListener: true }}
          initialContent={initialContent}
          readOnly={disabled}
          onTextChange={() => {
            if (quill) {
              const actualContent = quill.getContents().ops;
              onTextChange(actualContent);
            }
          }}
          autoFocus={autoFocus}
        />
        {quill && (
          <>
            <LinkCursorSelection />
            <MentionPicker quill={quill} readOnly={false} />
          </>
        )}
      </div>
    </div>
  );
};

interface CommentInputProps {
  autoFocus: boolean;
  initialContent: Op[];
  children?: (childProps: {
    input: JSX.Element;
    mentionUser: () => void;
    insertText: (text: string) => void;
  }) => React.ReactNode;
  onChange: (newContent: Op[]) => void;
  onSave: () => void;
  disabled?: boolean;
}

const CommentInput = ({
  autoFocus,
  initialContent = [],
  children,
  onChange,
  onSave,
  disabled,
}: CommentInputProps) => {
  const { quill } = useQuill();

  const input = (
    <CommentInputConsumerView
      quill={quill}
      initialContent={initialContent}
      autoFocus={autoFocus}
      onSave={onSave}
      onTextChange={onChange}
      disabled={!!disabled}
    />
  );

  return (
    <>
      {typeof children === 'function'
        ? children({
            input,
            mentionUser: () => {
              if (!quill) return;
              insertPrefix(quill, '@');
            },
            insertText: (text) => {
              if (quill) {
                insertText(quill, text);
              }
            },
          })
        : input}
    </>
  );
};

export default withQuillContextProvider(CommentInput);
